"use client";

import { useEffect, useId, useState, type ReactNode } from "react";
import { useLocale } from "next-intl";
import { ArrowRight, MapPin, VideoCamera } from "@phosphor-icons/react";
import { Link } from "@/lib/i18n/navigation";
import { Container } from "@/components/ui/Container";
import type { LivePhysiciansResponse } from "@/app/api/teleconsult/live-physicians/route";

export type LiveDoctorsLabels = {
  loading: string;
  online: string;
  conventioned: string;
  consult: string;
  /** Gabarit contenant {price}. */
  priceValue: string;
  priceOnRequest: string;
  /** Gabarit contenant {price} : plus bas tarif réel des médecins listés. */
  priceFrom?: string;
};

type Props = {
  labels: LiveDoctorsLabels;
  specialty?: string;
  city?: string;
  /** /live : état vide affiché, avec un renvoi vers les RDV vidéo de /teleconsultation. */
  empty?: { title: string; hint: string; cta: string; appointmentLink: string };
  /** /teleconsultation : bloc titré, absent tant qu'aucun médecin n'est en ligne. */
  section?: { title: string; intro: ReactNode };
};

const POLL_MS = 20_000;

/**
 * Médecins en ligne pour une téléconsultation immédiate, relus toutes les 20 s
 * (GET /api/teleconsult/live-physicians). Rien n'est rendu côté serveur : le HTML
 * des pages publiques est mis en cache, la présence d'un médecin ne doit pas l'être.
 */
export function LiveDoctorsList({ labels, specialty, city, empty, section }: Props) {
  const locale = useLocale();
  const headingId = useId();
  const [data, setData] = useState<LivePhysiciansResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const query = new URLSearchParams({ locale });
    if (specialty) query.set("specialty", specialty);
    if (city) query.set("city", city);

    async function load() {
      if (document.visibilityState === "hidden") return;
      try {
        const res = await fetch(`/api/teleconsult/live-physicians?${query.toString()}`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData((await res.json()) as LivePhysiciansResponse);
      } catch {
        // Une liste qu'on ne peut plus relire n'est plus fiable : personne n'est affiché en ligne.
        if (!controller.signal.aborted) setData({ physicians: [], fromPrice: null });
      }
    }

    load();
    const interval = setInterval(load, POLL_MS);
    document.addEventListener("visibilitychange", load);
    return () => {
      controller.abort();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", load);
    };
  }, [locale, specialty, city]);

  if (data === null) {
    if (section) return null;
    return (
      <div role="status" aria-busy="true" className="space-y-2">
        <span className="sr-only">{labels.loading}</span>
        <div aria-hidden className="h-[76px] rounded-[18px] bg-white ring-1 ring-[var(--sahha-border-subtle)] motion-safe:animate-pulse" />
      </div>
    );
  }

  const doctors = data.physicians;
  if (doctors.length === 0) {
    if (!empty) return null;
    return (
      <div className="rounded-[18px] bg-white p-6 text-center ring-1 ring-[var(--sahha-border-subtle)] sm:p-8">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
          <VideoCamera weight="duotone" size={26} aria-hidden />
        </span>
        <p className="mt-3 text-[15px] font-bold text-[var(--sahha-text-900)]">{empty.title}</p>
        <p className="mx-auto mt-1 max-w-md text-[13.5px] leading-snug text-[var(--sahha-text-600)]">{empty.hint}</p>
        <Link
          href="/teleconsultation"
          className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-[var(--sahha-primary-700)] px-5 text-[13.5px] font-bold text-white transition active:scale-[0.98]"
        >
          {empty.cta}
          <ArrowRight weight="bold" size={14} aria-hidden className="rtl:rotate-180" />
        </Link>
      </div>
    );
  }

  const priceFrom =
    labels.priceFrom && data.fromPrice ? (
      <p className="mb-3 text-[13px] font-semibold text-[var(--sahha-text-700)]">
        {labels.priceFrom.replace("{price}", data.fromPrice)}
      </p>
    ) : null;

  const list = (
    <ul className="space-y-3">
      {doctors.map((doc) => (
        <li key={doc.slug}>
          <article className="flex flex-col items-stretch gap-3 rounded-2xl border border-[var(--sahha-border-default)] bg-white p-4 sm:flex-row sm:items-center sm:p-5">
            <Link href={doc.href as never} className="flex min-w-0 flex-1 items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-100 text-base font-bold text-emerald-800">
                {doc.photoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={doc.photoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span aria-hidden>{doc.initials}</span>
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold text-[var(--sahha-text-900)]">{doc.name}</span>
                {doc.specialty && (
                  <span className="block truncate text-[13px] text-[var(--sahha-text-600)]">{doc.specialty}</span>
                )}
                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                  <span className="inline-flex items-center gap-1 font-semibold text-emerald-800">
                    <span aria-hidden className="h-2 w-2 rounded-full bg-emerald-500" />
                    {labels.online}
                  </span>
                  {doc.city && (
                    <span className="inline-flex items-center gap-1 text-[var(--sahha-text-600)]">
                      <MapPin size={12} weight="fill" aria-hidden /> {doc.city}
                    </span>
                  )}
                  {doc.conventioned && (
                    <span className="inline-flex rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                      {labels.conventioned}
                    </span>
                  )}
                </span>
              </span>
            </Link>

            <Link
              href={`/teleconsult/start?slug=${encodeURIComponent(doc.slug)}` as never}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-emerald-50 px-5 py-3.5 text-emerald-800 transition hover:bg-emerald-100 active:scale-[0.98] sm:w-auto sm:min-w-[180px] sm:flex-col sm:gap-1"
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <VideoCamera size={18} weight="fill" aria-hidden />
              </span>
              <span className="flex flex-col items-start sm:items-center">
                <span className="text-[14px] font-bold leading-none">
                  {labels.consult}
                  <span className="sr-only"> {doc.name}</span>
                </span>
                <span className="mt-1 text-[11.5px] font-semibold">
                  {doc.price ? labels.priceValue.replace("{price}", doc.price) : labels.priceOnRequest}
                </span>
              </span>
            </Link>
          </article>
        </li>
      ))}
    </ul>
  );

  if (section) {
    return (
      <Container className="pt-10">
        <section
          aria-labelledby={headingId}
          className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-6 sm:p-8"
        >
          <div className="mb-1 flex items-center gap-3">
            <span aria-hidden className="relative inline-flex h-3 w-3 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
            </span>
            <h2 id={headingId} className="text-[22px] font-extrabold tracking-tight text-[var(--sahha-text-900)]">
              {section.title}
            </h2>
          </div>
          <p className="text-[14px] text-[var(--sahha-text-600)]">{section.intro}</p>
          <div className="mt-5">
            {priceFrom}
            {list}
          </div>
        </section>
      </Container>
    );
  }

  return (
    <>
      {priceFrom}
      {list}
      {empty && (
        <Link
          href="/teleconsultation"
          className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[var(--sahha-primary-700)] underline-offset-2 hover:underline"
        >
          {empty.appointmentLink}
          <ArrowRight weight="bold" size={13} aria-hidden className="rtl:rotate-180" />
        </Link>
      )}
    </>
  );
}
